import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { AIPromptUsage } from '../../../models/AIPromptTemplate';
import { AIPromptField, useAIPromptField } from '../../../shared/components/ai/AIPromptField';
import { Button } from '../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { useRepCoachingGoals } from '../../voice-agent/hooks/useRepCoachingGoals';
import { useRepCoachingGoalsMutations } from '../../voice-agent/hooks/useRepCoachingGoalsMutations';
import { styleSheet } from './styles';

interface RepCoachingGoalsContentProps {
	userId: string;
	userName: string;
	onClose?: () => void;
}

const DIFFICULTY_OPTIONS = [
	{ value: 1, label: 'Easy' },
	{ value: 2, label: 'Medium' },
	{ value: 3, label: 'Hard' },
];

export const RepCoachingGoalsContent: React.FC<RepCoachingGoalsContentProps> = ({ userId, userName, onClose }) => {
	const { data: existingGoals, isLoading } = useRepCoachingGoals(userId);
	const { create, update } = useRepCoachingGoalsMutations();

	const [workingOn, setWorkingOn] = useState('');
	const [difficulty, setDifficulty] = useState(1);
	const [isSaving, setIsSaving] = useState(false);
	const workingOnField = useAIPromptField({ onChange: setWorkingOn, value: workingOn });

	// Reset form when goals are loaded
	useEffect(() => {
		if (existingGoals) {
			setWorkingOn(existingGoals.workingOn || '');
			setDifficulty(existingGoals.tysonMaxDifficulty || 1);
		} else {
			setWorkingOn('');
			setDifficulty(1);
		}
	}, [existingGoals]);

	const handleSave = useCallback(async () => {
		try {
			setIsSaving(true);

			await (existingGoals?.id
				? // Update existing goals
					update.mutateAsync({
						id: existingGoals.id,
						workingOn,
						tysonMaxDifficulty: difficulty,
					})
				: // Create new goals
					create.mutateAsync({
						userId,
						workingOn,
						tysonMaxDifficulty: difficulty,
					}));

			onClose?.();
		} catch (error) {
			console.error('Failed to save coaching goals:', error);
		} finally {
			setIsSaving(false);
		}
	}, [existingGoals, workingOn, difficulty, userId, update, create, onClose]);

	const handleCancel = useCallback(() => {
		onClose?.();
	}, [onClose]);

	if (isLoading) {
		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='tiny' />
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<div className={css(styleSheet.headerTitle)}>Rep Coaching Goals</div>
			</div>

			<div className={css(styleSheet.content)}>
				<div className={css(styleSheet.subtitle)}>Set coaching goals and difficulty for {userName}</div>

				<div className={css(styleSheet.field)}>
					<label className={css(styleSheet.label)} htmlFor='workingOn'>
						Coaching Goals:
					</label>
					<AIPromptField
						disabled={isSaving}
						field={workingOnField}
						id='workingOn'
						placeholder='Enter what this rep should be working on...'
						rows={4}
						textAreaStyles={[styleSheet.textarea]}
						usage={AIPromptUsage.RepCoaching}
					/>
				</div>

				<div className={css(styleSheet.field)}>
					<label className={css(styleSheet.label)} htmlFor='difficulty'>
						Tyson Difficulty Level:
					</label>
					<select
						id='difficulty'
						className={css(styleSheet.select)}
						value={difficulty}
						onChange={e => setDifficulty(Number(e.target.value))}
						disabled={isSaving}
					>
						{DIFFICULTY_OPTIONS.map(option => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>

				<div className={css(styleSheet.buttonSection)}>
					<Button onClick={handleCancel} disabled={isSaving} variant='secondary' size='lg'>
						Cancel
					</Button>
					<Button onClick={handleSave} disabled={isSaving || !!workingOnField.unfilledPlaceholder} size='lg'>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
				</div>
			</div>
		</div>
	);
};
